import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import React, { JSX } from "react";

import * as Types from "../data-types/api-client/api-products";
import * as Contact from "../data-types/api-client/api-contacts";
import { formatCurrency } from "../utils/numberFormatter"; // Adjust the import based on your project structure
import { formatDate } from "../utils/dateFormatter";

Font.register({
  family: "Roboto",
  fonts: [
    { src: "/fonts/Roboto-VariableFont_wdth,wght.ttf", fontWeight: "normal" },
    {
      src: "/fonts/Roboto-Bold.ttf",
      fontWeight: "bold",
    },
    {
      src: "/fonts/Roboto-Italic.ttf",
      fontWeight: "normal",
      fontStyle: "italic",
    },
  ],
});

// Define styles
const styles = StyleSheet.create({
  page: {
    fontFamily: "Roboto",
    fontWeight: "normal",
    flexDirection: "column",
    padding: 16, // tailwind's p-4
    backgroundColor: "#f7fafc", // tailwind's bg-gray-100
  },
  tableRow: {
    flexDirection: "row",
    backgroundColor: "#ffffff", // tailwind's bg-white
    height: "2rem",
  },
  tableHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#ffffff", // tailwind's bg-white
  },
  tableCol: {
    flex: 1,
    justifyContent: "center",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderColor: "#e2e8f0", // tailwind's border-gray-300
    padding: 3, // tailwind's p-2
  },
  tableColAdditionalInfo: {
    flexDirection: "column",
    borderStyle: "solid",
    padding: 3, // tailwind's p-2
  },

  tableColProductName: {
    flex: 1,
    justifyContent: "center",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderColor: "#e2e8f0", // tailwind's border-gray-300
    padding: 5, // tailwind's p-2
    maxWidth: "8rem",
  },
  tableHeaderColPrice: {
    justifyContent: "center",
    fontWeight: "normal",
    backgroundColor: "#203f63", // tailwind's bg-gray-200
    paddingTop: 8, // tailwind's p-2
    paddingBottom: 8,
    paddingLeft: 6,
    paddingRight: 6,
    width: "3.2rem",
  },
  tableHeaderColProducName: {
    justifyContent: "center",
    fontWeight: "normal",
    backgroundColor: "#203f63", // tailwind's bg-gray-200
    padding: 8, // tailwind's p-2
    width: "8rem",
  },
  tableColPrice: {
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderColor: "#e2e8f0", // tailwind's border-gray-300
    paddingTop: 8, // tailwind's p-2
    paddingBottom: 8,
    paddingLeft: 6,
    paddingRight: 6,
    width: "3.2rem",
    textAlign: "right",
  },
  tableHeaderCol: {
    flex: 1,
    justifyContent: "center",
    fontWeight: "normal",
    backgroundColor: "#203f63", // tailwind's bg-gray-200
    padding: 8, // tailwind's p-2
  },
  tableCell: {
    fontSize: 10, // equivalent to text-xs
    color: "#4a5568", // tailwind's text-gray-700
  },
  tableCellAdditionalInfoHeader: {
    fontSize: 10, // equivalent to text-xs
    color: "#4a5568", // tailwind's text-gray-700},
    marginTop: 10,
    fontWeight: "bold",
  },
  tableCellAdditionalInfo: {
    fontSize: 10, // equivalent to text-xs
    color: "#4a5568", // tailwind's text-gray-700},
    marginTop: 1,
    marginBottom: 1,
  },
  tableAccessoryCell: {
    fontSize: 10, // equivalent to text-xs
    fontStyle: "italic",
    color: "#4a5568", // tailwind's text-gray-700
  },
  tableContactCell: {
    fontSize: 12, // equivalent to text-xs
    color: "#4a5568", // tailwind's text-gray-700
    paddingTop: 2,
    paddingBottom: 2,
  },
  tableContactCellBold: {
    fontSize: 12, // equivalent to text-xs
    color: "#4a5568", // tailwind's text-gray-700
    paddingTop: 2,
    paddingBottom: 2,
    fontWeight: "bold",
  },
  tableHeaderCell: {
    fontSize: 10, // equivalent to text-xs
    color: "#f0f2f5", // tailwind's text-gray-800
    fontWeight: "bold", // equivalent to font-medium
  },
  tableHeaderCellProductName: {
    fontSize: 10, // equivalent to text-xs
    color: "#f0f2f5", // tailwind's text-gray-800
    fontWeight: "bold", // equivalent to font-
    width: 10,
  },
  tableHeaderContactCell: {
    fontSize: 12, // equivalent to text-xs
    color: "#f0f2f5", // tailwind's text-gray-800
    fontWeight: "bold", // equivalent to font-medium
  },
  categoryRow: {
    padding: 10,
    fontWeight: "bold",
    backgroundColor: "#f0f2f5",
    color: "#00235a",
    height: "35px",
  },
  categoryCell: {
    fontSize: 12,
  },
});

const PriceListPDF = ({
  categories,
  devices,
  contacts,
  priceList,
}: {
  categories: Types.RentCategory[] | undefined;
  devices: Types.RentDevice[] | undefined;
  contacts: Contact.ContactData | undefined;
  priceList: Types.RentPriceList | undefined;
}) => {
  const rowsPerPage = 22; // Define the max number of rows per page
  const pages: JSX.Element[] = [];
  let currentPage: JSX.Element[] = [];
  let currentRowCount = 0;
  let isLastPage = false;

  // Function to add a new page
  const addPage = () => {
    if (currentPage.length > 0) {
      pages.push(
        <Page size="A4" style={styles.page}>
          {currentPage}
        </Page>
      );
      currentPage = []; // Reset for the next set of rows
      currentRowCount = 0; // Reset row count
    }
  };

  /* Contact Headers */
  currentPage.push(
    <View>
      <View style={styles.tableHeaderRow}>
        <View style={styles.tableHeaderCol}>
          <Text style={styles.tableHeaderContactCell}>
            {contacts?.contact.address.name}
          </Text>
        </View>
      </View>

      <View style={styles.tableHeaderRow}>
        <View style={styles.tableCol}>
          <Text style={styles.tableContactCell}>
            {`${contacts?.contact.address.street} ${contacts?.contact.address.number}, ${contacts?.contact.address.psc} ${contacts?.contact.address.village}`}
          </Text>
          <Text style={styles.tableContactCellBold}>
            {`Prevádzka: ${contacts?.contact.address.dispensingPoint}`}
          </Text>
          <Text style={styles.tableContactCell}>
            {`IČO: ${contacts?.contact.ico}`}{" "}
          </Text>
          {priceList?.validFrom && (
            <Text style={styles.tableContactCell}>
              {`Cenník platný od: ${formatDate(priceList?.validFrom)}`}
            </Text>
          )}
          <Text style={styles.tableContactCell}>
            {priceList?.manager?.function}: {priceList?.manager?.name}
          </Text>
        </View>
      </View>
    </View>
  );
  currentRowCount++;

  /* Product's table header */
  currentPage.push(
    <View style={styles.tableHeaderRow}>
      <View style={styles.tableHeaderColProducName}>
        <Text style={styles.tableHeaderCell}>Položka</Text>
      </View>
      <View style={styles.tableHeaderCol}>
        <Text style={styles.tableHeaderCell}>Špecifikácia</Text>
      </View>
      <View style={styles.tableHeaderColPrice}>
        <Text style={styles.tableHeaderCell}>Nájomné na 5 hod.</Text>
      </View>
      <View style={styles.tableHeaderColPrice}>
        <Text style={styles.tableHeaderCell}>Nájomné na 24 hod.</Text>
      </View>
      <View style={styles.tableHeaderColPrice}>
        <Text style={styles.tableHeaderCell}>
          Kaucia<Text style={{ fontSize: 10 }}>*</Text>{" "}
        </Text>
      </View>
    </View>
  );
  currentRowCount += 4;

  /* Products table */
  categories?.forEach((category, categoryIndex) => {
    if (currentRowCount >= rowsPerPage) {
      isLastPage = false;
      addPage(); // Create a new page if needed
    }
    currentPage.push(
      <View key={category.id} style={styles.categoryRow}>
        <Text style={styles.categoryCell}>{category.name}</Text>
      </View>
    );
    currentRowCount++;

    devices
      ?.filter((device) => device.rentCategoryId === category.id)
      .forEach((device) => {
        // Check if adding another device would exceed the maximum rows per page
        if (currentRowCount >= rowsPerPage) {
          isLastPage = false;
          addPage(); // Create a new page if needed
        }

        // Push device row into the current page
        currentPage.push(
          <View style={styles.tableRow}>
            <View style={styles.tableColProductName}>
              <Text style={styles.tableCell}>{device.name}</Text>
            </View>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>
                {`${
                  device.model && device.description
                    ? device.model + ", " + device.description
                    : device.model && !device.description
                    ? device.model
                    : !device.model && device.description
                    ? device.description
                    : ""
                }`}
              </Text>
            </View>

            <View style={styles.tableColPrice}>
              {device.price5h && (
                <Text style={styles.tableCell}>
                  {formatCurrency(device.price5h)}
                </Text>
              )}
            </View>
            <View style={styles.tableColPrice}>
              {device.type === Types.RentType.RENT && device.price24h ? (
                <Text style={styles.tableCell}>
                  {formatCurrency(device.price24h)}
                </Text>
              ) : (
                device.type === Types.RentType.SERVICE &&
                device.price && (
                  <Text style={styles.tableCell}>
                    {formatCurrency(device.price)}
                  </Text>
                )
              )}
            </View>
            <View style={styles.tableColPrice}>
              <Text style={styles.tableCell}>
                {device.foreignerDeposit
                  ? formatCurrency(device.foreignerDeposit)
                  : ""}
              </Text>
            </View>
          </View>
        );
        currentRowCount++; // Increment row count for the device row

        // Accessory Mapping
        if (device.accessories && device.accessories.length > 0) {
          device.accessories.forEach((accessory, accessoryIndex) => {
            // Check if we need another page before adding accessories
            if (currentRowCount >= rowsPerPage) {
              isLastPage = false;
              addPage(); // Create a new page if needed
            }

            // Push accessory row into the current page
            currentPage.push(
              <View
                style={styles.tableRow}
                key={`${device.name}-${accessoryIndex}`}
              >
                <View style={styles.tableColProductName}>
                  <Text style={styles.tableAccessoryCell}></Text>{" "}
                  {/* Empty cell for alignment */}
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableAccessoryCell}>
                    {accessory.name}
                  </Text>
                </View>
                <View style={styles.tableColPrice}>
                  {accessory.price5h && (
                    <Text style={styles.tableCell}>
                      {formatCurrency(accessory.price5h)}
                    </Text>
                  )}
                </View>
                <View style={styles.tableColPrice}>
                  {accessory.type === Types.RentType.RENT &&
                  accessory.price24h ? (
                    <Text style={styles.tableCell}>
                      {formatCurrency(accessory.price24h)}
                    </Text>
                  ) : (
                    accessory.type === Types.RentType.SERVICE &&
                    accessory.price && (
                      <Text style={styles.tableCell}>
                        {formatCurrency(accessory.price)}
                      </Text>
                    )
                  )}
                </View>
                <View style={styles.tableColPrice}>
                  <Text style={styles.tableAccessoryCell}>
                    {accessory.foreignerDeposit
                      ? formatCurrency(accessory.foreignerDeposit)
                      : ""}
                  </Text>
                </View>
              </View>
            );

            currentRowCount++; // Increment row count for the accessory row
          });
        }
      });

    if (categoryIndex === categories.length - 1) {
      isLastPage = true; // Set flag to true for the last category
    }
  });

  if (isLastPage) {
    if (currentRowCount >= rowsPerPage) {
      addPage(); // Create a new page if needed
    }
    currentPage.push(
      <View style={styles.tableColAdditionalInfo}>
        <Text style={styles.tableCell}>
          <Text style={{ fontSize: 10 }}>{`* `}</Text>Kaucia pre občanov mimo okresy 
          TS, NO a DK
        </Text>

        {priceList?.additionalInformations?.map((info) => (
          <View style={styles.tableColAdditionalInfo}>
            <Text style={styles.tableCellAdditionalInfoHeader}>
              {info.name}
            </Text>
            {info.items.map((item) => (
              <Text style={styles.tableCellAdditionalInfo}>
                {`• ${item.item}`}
              </Text>
            ))}
          </View>
        ))}
      </View>
    );
    currentRowCount++; // Increment row count for the footer
  }

  addPage();

  return <Document>{pages}</Document>;
};

export default PriceListPDF;
